import React, { useState } from 'react';
import { Tooltip, Typography } from '@mui/material';
import type { TreeItemContentProps, TreeItemProps } from '@mui/x-tree-view';
import { TreeItem } from '@mui/x-tree-view';

import type { GroupedCodingResult, SentimentCode } from 'src/types/dashboard';
import { useCodingResults } from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import CommentsListModal from 'src/components/dashboard/DashboardPage/components/CommentsListModal';
import getTopicIdList from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/TreeItem/getTopicIdList';
import SentimentBar from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/TreeItem/SentimentBar';
import type {
  OpenCommentFilter,
  SentimentFilter,
} from 'src/components/dashboard/DashboardPage/widgets/OpenCommentsWidget/types';

import HRQSOpenCommentBar from '../HRQSOpenCommentsWidget/HRQSOpenCommentBar';

import { styles } from './index.styles';

export interface OpenCommentsTreeItemProps extends TreeItemProps {
  question: string | null;
  highestCount: number;
  level: number;
  openCommentFilter: OpenCommentFilter;
  treeData: GroupedCodingResult;
  treeLabel: string;
  design: 'default' | 'hrqs';
  treeLabelAddition?: string;
  showTranslationToggle?: boolean;
}

// https://github.com/mui/mui-x/issues/9959
declare module '@mui/x-tree-view/TreeItem' {
  interface TreeItemContentProps {
    highestCount: number;
    level: number;
    sentimentFilter: SentimentFilter;
    treeData: GroupedCodingResult;
    handleSelectionClick: (
      topicSentimentId: null | string,
      sentiment?: SentimentCode,
    ) => void;
    isComparison?: boolean;
  }
}
export const OPEN_COMMENTS_TREE_ITEM_TEST_ID =
  'open-comments-tree-item-test-id';

export const OPEN_COMMENTS_TREE_ITEM_GRID_TEST_ID =
  'open-comments-tree-item-grid-test-id';

const OpenCommentsTreeItem = React.forwardRef(function OpenCommentsTreeItem(
  {
    question,
    openCommentFilter,
    treeData,
    treeLabel,
    treeLabelAddition,
    sx,
    children,
    level,
    highestCount,
    design,
    showTranslationToggle,
    ...props
  }: OpenCommentsTreeItemProps,
  ref: React.ForwardedRef<HTMLLIElement>,
) {
  const [modalOpen, setModalOpen] = useState(false);
  const [topics, setTopics] = useState<Array<string>>([]);
  const { textFilter } = useCodingResults();

  const handleSelectionClick = (
    topicSentimentId: null | string,
    sentiment?: SentimentCode,
  ) => {
    setModalOpen(true);
    const topics = getTopicIdList(treeData, topicSentimentId, sentiment).filter(
      (topic) => topic !== null,
    );
    setTopics(topics);
  };

  return (
    <>
      <TreeItem
        className="OpenCommentsTreeItem-root"
        data-test-id={OPEN_COMMENTS_TREE_ITEM_TEST_ID}
        ref={ref}
        {...props}
        ContentComponent={
          design === 'default' ? SentimentBar : HRQSOpenCommentBar
        }
        ContentProps={
          {
            sentimentFilter: openCommentFilter.sentimentFilter,
            treeData,
            sx,
            children,
            highestCount,
            level,
            handleSelectionClick,
          } as Omit<TreeItemContentProps, 'classes' | 'itemId'>
        }
        label={
          <Tooltip
            arrow
            enterTouchDelay={1}
            leaveTouchDelay={3000}
            placement="top"
            title={
              treeLabelAddition
                ? `${treeLabel} (${treeLabelAddition})`
                : treeLabel
            }
          >
            <Typography
              sx={{
                overflow: 'hidden',
                pl: level < 1 ? 0 : 2,
                pr: 1,
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {treeLabelAddition
                ? `${treeLabel} (${treeLabelAddition})`
                : treeLabel}
            </Typography>
          </Tooltip>
        }
        sx={[styles.muiTreeItemRoot, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {children}
      </TreeItem>

      <CommentsListModal
        filterData={openCommentFilter}
        question={question}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        topics={topics}
        initialTextFilter={textFilter}
        showTranslationToggle={showTranslationToggle}
      />
    </>
  );
});

export default OpenCommentsTreeItem;
